import {Box, useTheme} from '@mui/material';
import {useMemo, useRef, useEffect, useState} from 'react';
import {NumberLineElement, NumberLineGraphProps} from './types';

export default function NumberLineGraph({range, lines}: NumberLineGraphProps) {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement>(null);
  const [min, max] = range;
  const rangeSpan = max - min;

  // Helper to resolve color (theme color or CSS color)
  const resolveColor = (color: string): string => {
    if (color.startsWith('#') || color.startsWith('rgb')) {
      return color;
    }
    // Try to resolve as theme color
    const colorParts = color.split('.');
    if (colorParts.length === 2) {
      const [paletteKey, colorKey] = colorParts;
      const palette = theme.palette[paletteKey as keyof typeof theme.palette];
      if (palette && typeof palette === 'object' && colorKey in palette) {
        return (palette as any)[colorKey];
      }
    }
    return color;
  };

  const getLineKey = (line: NumberLineElement): string => {
    return [
      'interval',
      line.start,
      line.end === undefined ? 'point' : line.end,
      line.startOpen ? 'open' : 'closed',
      line.end === undefined ? '' : line.endOpen ? 'open' : 'closed',
      line.label ?? '',
      line.color ?? '',
      line.offset ?? '',
    ].join('|');
  };

  // Calculate tick marks - show integers and key values
  const tickMarks = useMemo(() => {
    const ticks: number[] = [];
    const tickStep = rangeSpan <= 10 ? 1 : rangeSpan <= 50 ? 5 : rangeSpan <= 100 ? 10 : 20;

    for (let i = Math.ceil(min / tickStep) * tickStep; i <= max; i += tickStep) {
      if (i >= min && i <= max) {
        ticks.push(i);
      }
    }

    // Ensure min and max are always shown
    if (!ticks.includes(min)) {
      ticks.unshift(min);
    }
    if (!ticks.includes(max)) {
      ticks.push(max);
    }
    ticks.sort((a, b) => a - b);
    return ticks;
  }, [min, max, rangeSpan]);

  // Calculate position as percentage (0-100)
  const getPosition = (value: number): number => {
    return ((value - min) / rangeSpan) * 100;
  };

  // SVG dimensions - calculate height based on number of lines to accommodate offsets
  const maxOffset = lines.length > 0 ? Math.max(...lines.map((line, index) => line.offset ?? 30 + index * 25)) : 30;
  const svgHeight = Math.max(120, maxOffset + 80); // Ensure enough space above number line
  const svgPadding = {top: 20, bottom: 20, left: 20, right: 20};
  // Position number line near the bottom (80% from top, leaving space for labels below)
  const numberLineY = svgHeight * 0.8;
  const textColor = theme.palette.text.primary;

  // Calculate SVG width based on container
  const [svgWidth, setSvgWidth] = useState(800);

  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        const container = svgRef.current.parentElement;
        if (container) {
          setSvgWidth(container.clientWidth || 800);
        }
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const plotWidth = svgWidth - svgPadding.left - svgPadding.right;

  // Convert data value to SVG x coordinate
  const valueToX = (value: number): number => {
    return svgPadding.left + (getPosition(value) / 100) * plotWidth;
  };

  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        px: 1,
      }}
    >
      <svg
        ref={svgRef}
        width="100%"
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{display: 'block'}}
      >
        {/* Main horizontal number line */}
        <line
          x1={svgPadding.left}
          y1={numberLineY}
          x2={svgWidth - svgPadding.right}
          y2={numberLineY}
          stroke={textColor}
          strokeWidth={2}
        />

        {/* Tick marks */}
        {tickMarks.map((tick) => {
          const x = valueToX(tick);
          return (
            <g key={tick}>
              {/* Tick line */}
              <line x1={x} y1={numberLineY - 4} x2={x} y2={numberLineY + 4} stroke={textColor} strokeWidth={1} />
              {/* Tick label */}
              <text x={x} y={numberLineY + 18} textAnchor="middle" fontSize="12" fill={textColor}>
                {tick}
              </text>
            </g>
          );
        })}

        {/* Render intervals */}
        {lines.map((line, index) => {
          // Default to darker primary color
          const defaultColor = theme.palette.primary.dark || theme.palette.primary.main;
          const lineColor = line.color || defaultColor;
          const resolvedColor = resolveColor(lineColor);
          const lineKey = getLineKey(line);
          // Default offset: 30px base + 25px per index to prevent overlap
          // lines[0] is closest to number line, lines[1] is further above
          const offset = line.offset ?? 30 + index * 25;
          const markerY = numberLineY - offset;

          // If end is not provided, render as a single point
          if (line.end === undefined) {
            const x = valueToX(line.start);
            const markerRadius = line.startOpen ? 6 : 7;

            return (
              <g key={lineKey}>
                {/* Dashed vertical line from marker to number line */}
                <line
                  x1={x}
                  y1={markerY}
                  x2={x}
                  y2={numberLineY}
                  stroke={resolvedColor}
                  strokeWidth={1}
                  strokeDasharray="4,2"
                />
                {/* Marker circle */}
                <circle
                  cx={x}
                  cy={markerY}
                  r={markerRadius}
                  fill={line.startOpen ? theme.palette.background.paper : resolvedColor}
                  stroke={resolvedColor}
                  strokeWidth={2}
                />
                {/* Label */}
                {line.label && (
                  <text x={x} y={markerY - 8} textAnchor="middle" fontSize="11" fill={resolvedColor} fontWeight="500">
                    {line.label}
                  </text>
                )}
              </g>
            );
          }

          // If end is provided, render as a line segment
          const startX = valueToX(line.start);
          const endX = valueToX(line.end);
          const startRadius = line.startOpen ? 6 : 7;
          const endRadius = line.endOpen ? 6 : 7;

          return (
            <g key={lineKey}>
              {/* Horizontal line segment for the interval */}
              <line x1={startX} y1={markerY} x2={endX} y2={markerY} stroke={resolvedColor} strokeWidth={4} />
              {/* Start point marker */}
              <g>
                {/* Dashed vertical line from start marker to number line */}
                <line
                  x1={startX}
                  y1={markerY}
                  x2={startX}
                  y2={numberLineY}
                  stroke={resolvedColor}
                  strokeWidth={1}
                  strokeDasharray="4,2"
                />
                {/* Start marker circle */}
                <circle
                  cx={startX}
                  cy={markerY}
                  r={startRadius}
                  fill={line.startOpen ? theme.palette.background.paper : resolvedColor}
                  stroke={resolvedColor}
                  strokeWidth={2}
                />
              </g>
              {/* End point marker */}
              <g>
                {/* Dashed vertical line from end marker to number line */}
                <line
                  x1={endX}
                  y1={markerY}
                  x2={endX}
                  y2={numberLineY}
                  stroke={resolvedColor}
                  strokeWidth={1}
                  strokeDasharray="4,2"
                />
                {/* End marker circle */}
                <circle
                  cx={endX}
                  cy={markerY}
                  r={endRadius}
                  fill={line.endOpen ? theme.palette.background.paper : resolvedColor}
                  stroke={resolvedColor}
                  strokeWidth={2}
                />
              </g>
              {/* Label above the interval */}
              {line.label && (
                <text
                  x={(startX + endX) / 2}
                  y={markerY - 8}
                  textAnchor="middle"
                  fontSize="11"
                  fill={resolvedColor}
                  fontWeight="500"
                >
                  {line.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}
